import { type FC } from 'react';

/**
 * ACCLogo — the multi-product layered-glass mark used across the ACC
 * Agentic family.
 *
 * Each product uses the same three-layer construction from Apple iOS 26's
 * app-icon pattern:
 *   1. Base gradient rounded square (per-product color family)
 *   2. Specular top highlight (white fade)
 *   3. White glyph (per-product symbol)
 *   4. 1px inner edge stroke
 *
 * Corner radius is `size * 22/88` to maintain concentric curvature as the
 * icon scales. Use size={28} for nav, size={42} for avatar/KPI, size={64}
 * for login page hero.
 *
 * Adding a new product:
 *   1. Add an entry to PRODUCT_SPECS below (gradient + glyph render fn)
 *   2. Add the key to the Product union
 *   3. No other changes needed — the same construction applies
 */

export type Product =
  | 'acc'           // parent — concentric circles
  | 'humans'        // Agentic Humans — teal, person
  | 'compliance'    // Agentic Compliance — purple, shield + check
  | 'insurance'     // Insurance UW — amber, waveform
  | 'brand'         // Brand Management — rose, radar rings
  | 'loanaudit';    // Agentic Loan Audit — navy, magnifier over documents

export interface ACCLogoProps {
  product?: Product;
  size?: number;
  className?: string;
  title?: string;
}

interface ProductSpec {
  from: string;
  to: string;
  glyph: (s: number) => JSX.Element;
}

const PRODUCT_SPECS: Record<Product, ProductSpec> = {
  acc: {
    from: '#97144D',
    to: '#5E0C30',
    glyph: (s) => {
      const cx = s / 2;
      const cy = s / 2;
      // Concentric circles at radii 26, 16, 6 for size=64 — scale proportionally
      const r = (v: number) => (v * s) / 64;
      return (
        <g>
          <circle cx={cx} cy={cy} r={r(26)} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={r(1.5)} />
          <circle cx={cx} cy={cy} r={r(16)} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth={r(1.5)} />
          <circle cx={cx} cy={cy} r={r(6)} fill="rgba(255,255,255,0.95)" />
        </g>
      );
    },
  },
  humans: {
    from: '#0f766e',
    to: '#042f2c',
    glyph: (s) => {
      const cx = s / 2;
      const r = (v: number) => (v * s) / 64;
      // Head + shoulders silhouette
      return (
        <g fill="rgba(255,255,255,0.92)">
          <circle cx={cx} cy={r(22)} r={r(8)} />
          <path
            d={`M ${cx - r(14)} ${r(48)} Q ${cx} ${r(34)} ${cx + r(14)} ${r(48)} L ${cx + r(14)} ${r(56)} L ${cx - r(14)} ${r(56)} Z`}
          />
        </g>
      );
    },
  },
  compliance: {
    from: '#7c3aed',
    to: '#2a0f5f',
    glyph: (s) => {
      const cx = s / 2;
      const r = (v: number) => (v * s) / 64;
      // Shield with check
      return (
        <g fill="rgba(255,255,255,0.95)">
          <path d={`M ${cx} ${r(14)} L ${cx - r(14)} ${r(20)} L ${cx - r(14)} ${r(36)} Q ${cx - r(14)} ${r(46)} ${cx} ${r(52)} Q ${cx + r(14)} ${r(46)} ${cx + r(14)} ${r(36)} L ${cx + r(14)} ${r(20)} Z`} />
          <path
            d={`M ${cx - r(6)} ${r(32)} L ${cx - r(1)} ${r(38)} L ${cx + r(8)} ${r(26)}`}
            stroke={PRODUCT_SPECS_COLORS.complianceDark}
            strokeWidth={r(2.5)}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      );
    },
  },
  insurance: {
    from: '#d97706',
    to: '#4a2404',
    glyph: (s) => {
      const r = (v: number) => (v * s) / 64;
      // Waveform / heartbeat line
      return (
        <path
          d={`M ${r(12)} ${r(32)} L ${r(22)} ${r(32)} L ${r(28)} ${r(20)} L ${r(34)} ${r(44)} L ${r(40)} ${r(28)} L ${r(46)} ${r(32)} L ${r(54)} ${r(32)}`}
          stroke="rgba(255,255,255,0.95)"
          strokeWidth={r(2.5)}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    },
  },
  brand: {
    from: '#e11d48',
    to: '#4c0519',
    glyph: (s) => {
      const cx = s / 2;
      const cy = s / 2;
      const r = (v: number) => (v * s) / 64;
      // Radar rings
      return (
        <g stroke="rgba(255,255,255,0.85)" fill="none" strokeWidth={r(1.5)}>
          <circle cx={cx} cy={cy} r={r(8)} fill="rgba(255,255,255,0.95)" stroke="none" />
          <circle cx={cx} cy={cy} r={r(16)} />
          <circle cx={cx} cy={cy} r={r(24)} />
        </g>
      );
    },
  },
  loanaudit: {
    from: '#97144D',
    to: '#5E0C30',
    glyph: (s) => {
      const r = (v: number) => (v * s) / 64;
      // Document stack + magnifying glass over the corner
      return (
        <g>
          {/* Document lines */}
          <g stroke="rgba(255,255,255,0.55)" strokeWidth={r(1.6)} strokeLinecap="round">
            <line x1={r(16)} y1={r(22)} x2={r(42)} y2={r(22)} />
            <line x1={r(16)} y1={r(30)} x2={r(42)} y2={r(30)} />
            <line x1={r(16)} y1={r(38)} x2={r(34)} y2={r(38)} />
          </g>
          {/* Magnifying glass in the bottom-right */}
          <g stroke="rgba(255,255,255,0.95)" fill="none" strokeWidth={r(2.2)} strokeLinecap="round">
            <circle cx={r(44)} cy={r(44)} r={r(7)} />
            <line x1={r(50)} y1={r(50)} x2={r(56)} y2={r(56)} />
          </g>
        </g>
      );
    },
  },
};

// Color lookup helper used by the compliance glyph to get its dark stroke color
const PRODUCT_SPECS_COLORS = {
  complianceDark: '#2a0f5f',
};

export const ACCLogo: FC<ACCLogoProps> = ({
  product = 'acc',
  size = 32,
  className,
  title,
}) => {
  const spec = PRODUCT_SPECS[product];
  const r = (size * 22) / 88; // concentric radius formula
  const gradId = `acc-grad-${product}`;
  const highlightId = `acc-hl-${product}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title ?? `ACC ${product} logo`}
      className={className}
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={spec.from} />
          <stop offset="100%" stopColor={spec.to} />
        </linearGradient>
        <linearGradient id={highlightId} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>

      {/* Base gradient rounded square */}
      <rect width={size} height={size} rx={r} ry={r} fill={`url(#${gradId})`} />
      {/* Specular top highlight */}
      <rect width={size} height={size} rx={r} ry={r} fill={`url(#${highlightId})`} />
      {/* Product glyph */}
      {spec.glyph(size)}
      {/* Edge stroke for definition */}
      <rect
        width={size}
        height={size}
        rx={r}
        ry={r}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="1"
      />
    </svg>
  );
};

export default ACCLogo;
